import { DataTreeValue } from "./DataTreeValue";
import { ObjectDataDefinition } from "./Definitions/ObjectDataDefinition";
import { IDataDefinition } from "./Definitions/IDataDefinition";
import { IDataDefinitionOwner } from "./IDataDefinitionOwner";
import { StringDataDefinition } from "./Definitions/StringDataDefinition";
import { NumberDataDefinition } from "./Definitions/NumberDataDefinition";
import { BooleanDataDefinition } from "./Definitions/BooleanDataDefinition";
import { DataTypes } from "./DataTypes";
import { IConfigStorage } from "../IConfigStorage";
import { FileDataDefinition } from "./Definitions/FileDataDefinition";
import { DateDataDefinition } from "./Definitions/DateDataDefinition";

import { DataUtilities } from "../DataUtilities";
import { PropertyDataMemberAccessor } from "../DataMemberAccessor";
import { DataStructureDifferences } from "./ExecutionData/DataStructureDifferences";
import { DataDefinitionFieldMetaData } from "./DataDefinitionFieldMetaData";
import { BuilderStorageConstants } from "../../Plugins/BuilderStorage/BuilderStorageConstants";
import { AppGlueError } from "../../AppGlueError";
import { IDesignValidationProvider, ValidationIssue } from "../IDesignValidationProvider";
import { UndefinedDataDefinition } from "./Definitions/UndefinedDataDefinition";
import type { AppGlueAny } from "../AppGlueAny";

export class DataDefinition implements IDataDefinitionOwner, IConfigStorage, IDesignValidationProvider {
    private static TYPEMAPFILLED: boolean = false;
    fields: IDataDefinition[] = [];
    value: { [propertyName: string]: AppGlueAny } = {};
    fieldMetaData: { [name: string]: DataDefinitionFieldMetaData } = {};

    private static _TYPEMAP: { [key: string]: IDataDefinition } = {};

    static get TYPEMAP(): { [p: string]: IDataDefinition } {
        if (!this.TYPEMAPFILLED) this.fillTypeMap();

        return this._TYPEMAP;
    }

    // parses json schema into the object definitions.
    // - remove items that are not in schema
    // - adds items that are in schema
    // - adjust items that the schema changes values of
    // ** Throw error if schema is not valid

    // to add:
    //  - title
    //  - description
    //  - sub type on number
    // resolve url schema
    // resolve internal refs

    static parseSchemaNode(
        fullSchema: object,
        schemaNode: object,
        owner: IDataDefinitionOwner,
        existingFields: IDataDefinition[],
        removeItemsNotInSchema: boolean,
        reorderElementsToMatchSchema: boolean,
    ): IDataDefinition[] {
        //https://json-schema.org/understanding-json-schema/index.html

        let newFieldsMap: { [fieldName: string]: IDataDefinition } = {};
        let newFieldList: IDataDefinition[] = [];

        let oldFieldsMap: { [fieldName: string]: IDataDefinition } = {};

        for (let oField of existingFields) {
            if (oField.name) {
                oldFieldsMap[oField.name] = oField;
            }
        }

        // root might be array, not
        let properties = Reflect.get(schemaNode, "properties");

        for (let property in properties) {
            let propDescription = properties[property];
            let isList: boolean = false;

            if (propDescription.type === "array") {
                // https://json-schema.org/understanding-json-schema/reference/array.html
                // handle array
                // check items property
                // call recursively??
                // unique
                // single value
                // objects
                // tuples
                isList = true;
            }

            let def: IDataDefinition | undefined = this.parseProperty(
                propDescription,
                property,
                owner,
                oldFieldsMap,
                fullSchema,
                removeItemsNotInSchema,
                reorderElementsToMatchSchema,
                isList,
            );

            if (def && def.name) {
                newFieldList.push(def);
                newFieldsMap[def.name] = def;
            }
        }

        return DataDefinition.reconcileFieldList(
            removeItemsNotInSchema,
            reorderElementsToMatchSchema,
            newFieldList,
            newFieldsMap,
            existingFields,
        );
    }

    static reconcileFieldList(
        removeItemsNotInSchema: boolean,
        reorderElementsToMatch: boolean,
        newFieldList: IDataDefinition[],
        newFieldsMap: { [p: string]: IDataDefinition },
        existingFields: IDataDefinition[],
    ) {
        if (removeItemsNotInSchema && reorderElementsToMatch) {
            return newFieldList;
        } else if (reorderElementsToMatch && !removeItemsNotInSchema) {
            for (let oldField of existingFields) {
                if (oldField.name && !newFieldsMap[oldField.name]) {
                    newFieldList.push(oldField);
                }
            }
            return newFieldList;
        } else if (!reorderElementsToMatch && !removeItemsNotInSchema) {
            let toSet: IDataDefinition[] = [];

            for (let oldField of existingFields) {
                if (oldField.name) {
                    if (!newFieldsMap[oldField.name]) {
                        toSet.push(oldField);
                    } else {
                        toSet.push(newFieldsMap[oldField.name]);
                    }
                }
            }
            return toSet;
        } else {
            // if (!reorderElementsToMatch && removeItemsNotInSchema)
            let toSet: IDataDefinition[] = [];

            for (let oldField of existingFields) {
                if (oldField.name) {
                    if (!newFieldsMap[oldField.name]) {
                        // this field is being removed
                    } else {
                        toSet.push(newFieldsMap[oldField.name]);
                    }
                }
            }

            return toSet;
        }
    }

    static createFieldFromData(data: object): IDataDefinition | undefined {
        if (!data) return;

        let typeName = Reflect.get(data, BuilderStorageConstants.TYPE_FIELD_NAME);

        if (typeName) {
            let registeredControl = DataDefinition.TYPEMAP[typeName];
            let prototype = Object.getPrototypeOf(registeredControl);
            let val = new prototype.constructor();
            // @ts-ignore
            let cc = val as IDataDefinition;

            cc.setStorageData(data);

            return cc;
        }

        throw new Error("Could not create data definition: no type defined");
    }

    private static validateSchema(schema: string) {
        let schemaObj: object = {};
        try {
            schemaObj = JSON.parse(schema);
        } catch (error) {
            throw new Error("Invalid json schema");
        }

        if (Reflect.get(schemaObj, "type") !== "object") throw new Error("Invalid JSON Schema");

        if (Reflect.get(schemaObj, "allOf")) throw new Error("allOf is not supported");
        if (Reflect.get(schemaObj, "anyOf")) throw new Error("anyOf is not supported");
        if (Reflect.get(schemaObj, "oneOf")) throw new Error("oneOf is not supported");
        if (Reflect.get(schemaObj, "not")) throw new Error("not is not supported");

        let properties = Reflect.get(schemaObj, "properties");
        for (let property in properties) {
            let propDescription = properties[property];

            if (propDescription.type === "#ref") {
                // Throw error if still have #ref
                throw new Error("Ref should not be there anymore!");
            }
        }
    }

    private static parseProperty(
        propDescription: AppGlueAny,
        property: string,
        owner: IDataDefinitionOwner,
        oldFieldsMap: { [fieldName: string]: IDataDefinition },
        fullSchema: object,
        removeItemsNotInSchema: boolean,
        reorderElementsToMatchSchema: boolean,
        isList: boolean,
    ): IDataDefinition | undefined {
        let def: IDataDefinition | undefined = undefined;

        if (isList) propDescription = propDescription.items;

        if (propDescription.type === "string") {
            // todo: emum
            let sdef = new StringDataDefinition();
            if (oldFieldsMap[property]?.type === DataTypes.STRING) {
                // need to clone due to it's updating the old field also.
                // sdef = oldFieldsMap[property] as StringDataDefinition;
                sdef.setStorageData(oldFieldsMap[property].getStorageData());
            }
            sdef.name = property;
            sdef.list = isList;
            sdef.owner = owner;

            let pattern = Reflect.get(propDescription, "pattern");
            if (pattern) sdef.pattern = pattern;

            def = sdef;
        } else if (propDescription.type === "boolean") {
            let sdef = new BooleanDataDefinition();
            if (oldFieldsMap[property]?.type === DataTypes.BOOLEAN) {
                // need to clone as it's updating the old field also;
                // sdef = oldFieldsMap[property] as BooleanDataDefinition;
                sdef.setStorageData(oldFieldsMap[property].getStorageData());
            }
            sdef.name = property;
            sdef.list = isList;
            sdef.owner = owner;
            def = sdef;
        } else if (propDescription.type === "number" || propDescription.type === "integer") {
            let sdef = new NumberDataDefinition();
            if (oldFieldsMap[property]?.type === DataTypes.NUMBER) {
                // need to clone as it's updating the old field also;
                // sdef = oldFieldsMap[property] as NumberDataDefinition;
                sdef.setStorageData(oldFieldsMap[property].getStorageData());
            }
            sdef.name = property;
            sdef.list = isList;
            sdef.owner = owner;

            // check upper and lower bounds
            let upperBound = Reflect.get(propDescription, "maximum");
            if (upperBound) {
                sdef.upperBounds = +upperBound;
            }

            let lowerBound = Reflect.get(propDescription, "minimum");
            if (lowerBound) {
                sdef.lowerBounds = +lowerBound;
            }

            sdef.allowDecimals = propDescription.type === "number";

            def = sdef;
        } else if (propDescription.type === "null") {
            // ignore
        } else if (propDescription.type === "object") {
            let odef = new ObjectDataDefinition();
            if (oldFieldsMap[property]?.type === DataTypes.OBJECT) {
                // need to clone as it's updating the old field also;
                // odef = oldFieldsMap[property] as ObjectDataDefinition;
                odef.setStorageData(oldFieldsMap[property].getStorageData());
            }
            odef.name = property;
            odef.list = isList;
            odef.owner = owner;
            odef.fields = this.parseSchemaNode(
                fullSchema,
                propDescription,
                owner,
                (oldFieldsMap[property] as ObjectDataDefinition)?.fields ?? [],
                removeItemsNotInSchema,
                reorderElementsToMatchSchema,
            );
            def = odef;
        } else if (FileDataDefinition.isValidJSONObject(propDescription)) {
            let fdef = new FileDataDefinition();
            if (oldFieldsMap[property]?.type === DataTypes.FILE) {
                fdef.setStorageData(oldFieldsMap[property].getStorageData());
            }
            fdef.name = property;
            fdef.list = isList;
            fdef.owner = owner;

            def = fdef;
        }
        return def;
    }

    private static fillTypeMap() {
        DataDefinition.TYPEMAPFILLED = true;
        DataDefinition._TYPEMAP[DataTypes.STRING] = new StringDataDefinition();
        DataDefinition._TYPEMAP[DataTypes.NUMBER] = new NumberDataDefinition();
        DataDefinition._TYPEMAP[DataTypes.OBJECT] = new ObjectDataDefinition();
        DataDefinition._TYPEMAP[DataTypes.BOOLEAN] = new BooleanDataDefinition();
        DataDefinition._TYPEMAP[DataTypes.FILE] = new FileDataDefinition();
        DataDefinition._TYPEMAP[DataTypes.DATE] = new DateDataDefinition();
        DataDefinition._TYPEMAP[DataTypes.UNDEFINED] = new UndefinedDataDefinition();
    }

    // parses json schema into the object definitions.
    // ** JSON is evaluated to see to determine the type of each element **
    // - remove items that are not in schema
    // - adds items that are in schema
    // - adjust items that the schema changes values of
    // ** Throw error if json is not valid

    // returns valid json schema from object definition
    toJSONSchema(fields?: IDataDefinition[]): string {
        let schema: {
            type: string;
            properties: { [propertyName: string]: AppGlueAny };
        } = {
            type: "object",
            properties: {},
        };
        if (!fields) {
            fields = this.fields;
        }

        for (let def of fields) {
            Reflect.set(schema.properties, def.name, def.toJSONSchema());

            // if (def.list) {
            //     if ([DataTypes.STRING, DataTypes.NUMBER, DataTypes.BOOLEAN, DataTypes.DATE].includes(def.type)) {
            //         Reflect.set(schema.properties, def.name, {
            //             type: "array",
            //             items: { type: def.type },
            //         });
            //     } else if (def.type === DataTypes.OBJECT) {
            //         Reflect.set(schema.properties, def.name, {
            //             type: "array",
            //             items: JSON.parse(this.toJSONSchema(def.fields)),
            //         });
            //     }
            // } else {
            //     if ([DataTypes.STRING, DataTypes.NUMBER, DataTypes.BOOLEAN, DataTypes.DATE].includes(def.type)) {
            //         Reflect.set(schema.properties, def.name, {
            //             type: def.type,
            //         });
            //     } else if (def.type === DataTypes.OBJECT) {
            //         Reflect.set(schema.properties, def.name, JSON.parse(this.toJSONSchema(def.fields)));
            //     }
            // }

            // if (def instanceof FileDataDefinition) {
            //     Reflect.set(schema.properties, def.name, def.toJSONSchema());
            // }
        }
        return JSON.stringify(schema, null, 2);
    }

    // returns valid json schema from object definition
    toJSONObject(dataObj?: object, fields?: IDataDefinition[]): string {
        let schema: {
            [propertyName: string]: AppGlueAny;
        } = {};
        if (!fields) {
            fields = this.fields;
        }

        if (!dataObj) {
            dataObj = this.value;
        }

        let tempObj = new ObjectDataDefinition();
        tempObj.fields = fields;
        schema = tempObj.toJSONObject(dataObj);

        return JSON.stringify(schema, null, 2);
    }

    toStructureString(indent?: number): string {
        return `${this.fields.map((f) => f.toStructureString(indent || 0)).join("")}`;
    }

    fromJSONObject(obj: object) {
        let tempObj = ObjectDataDefinition.fromJSONObjectValue(obj) as ObjectDataDefinition;

        if (tempObj) {
            this.fields = tempObj.fields;
        }

        this.value = obj;
    }

    // one of?
    mergeJSONSchema(
        schema: string,
        removeItemsNotInSchema: boolean = false,
        reorderElementsToMatchSchema: boolean = true,
    ): void {
        DataDefinition.validateSchema(schema);
        let schemaObj = JSON.parse(schema);

        this.fields = DataDefinition.parseSchemaNode(
            schemaObj,
            schemaObj,
            this,
            this.fields,
            removeItemsNotInSchema,
            reorderElementsToMatchSchema,
        );
    }

    // ** change order of elements to match json
    mergeJSON(json: string, removeItemsNotInSchema: boolean = false, reorderElementsToMatch: boolean = true): void {
        try {
            this.mergeObject(JSON.parse(json), removeItemsNotInSchema, reorderElementsToMatch);
        } catch (error: AppGlueAny) {
            throw new Error(error);
        }
    }

    // updates data definition with values from object
    mergeObject(data: object, removeItemsNotInSchema: boolean = false, reorderElementsToMatch: boolean = true) {
        this.fields = ObjectDataDefinition.parseFieldsForObject(
            this,
            data,
            this.fields,
            removeItemsNotInSchema,
            reorderElementsToMatch,
        );
    }

    // returns json as string
    toSampleJson(): string {
        return JSON.stringify(this.toSampleObject(), null, 2);
    }

    // build object from data definition
    toSampleObject(): object {
        return this.value;
    }

    getFieldByDataPath(dataPath: string): IDataDefinition | undefined {
        const dataPathItems = dataPath.split(".");
        let fields = this.fields;
        let index = 0;
        for (const dataPathItem of dataPathItems) {
            const matchedField = fields.find((field) => field.name === dataPathItem);
            if (index === dataPathItems.length - 1) {
                return matchedField;
            } else if (matchedField?.type !== DataTypes.OBJECT) {
                throw new Error("Datapath is not valid");
            } else {
                fields = (matchedField as ObjectDataDefinition).fields;
                index++;
            }
        }
        return undefined;
    }

    // eslint-disable-next-line
    getDataValues(type: DataTypes | {}, isList: boolean): DataTreeValue[] {
        return [];
    }

    setValue(dataPath: string, value: AppGlueAny) {
        let paths = DataUtilities.splitPath(dataPath);

        let dataFieldPath = paths
            .filter((p) => p instanceof PropertyDataMemberAccessor)
            .map((p) => (p as PropertyDataMemberAccessor).propertyName)
            .join(".");

        // validate
        let currentField = DataUtilities.getFieldFromDataDefinitionByName(this.fields, dataFieldPath);
        if (!currentField) {
            throw new Error(`invalid data path [${dataPath}]`);
        }

        DataUtilities.set(this.getValueObject(), dataPath, value, false, true, true);
    }

    getFieldMetaData(field: string): DataDefinitionFieldMetaData {
        let metaData: DataDefinitionFieldMetaData = this.fieldMetaData[field];

        return metaData ?? new DataDefinitionFieldMetaData(field);
    }

    setFieldMetaData(metaData: DataDefinitionFieldMetaData) {
        if (!metaData.fieldName) throw new AppGlueError("Field name not specified");

        this.fieldMetaData[metaData.fieldName] = metaData;
    }

    getValueObject(): { [p: string]: AppGlueAny } {
        return this.value;
    }

    getStorageData(): object {
        let storage = {};

        let fields: object[] = [];

        for (let f of this.fields) {
            let d = f.getStorageData();
            BuilderStorageConstants.verifyTypeField(d);
            fields.push(d);
        }

        let metaData: DataDefinitionFieldMetaData[] = [];

        for (let m of Object.values(this.fieldMetaData)) {
            metaData.push(m);
        }

        Reflect.set(storage, "metadata", metaData);
        Reflect.set(storage, "fieldList", fields);
        Reflect.set(storage, "value", this.value);
        return storage;
    }

    setStorageData(data: object) {
        let defArray = Reflect.get(data, "fieldList");
        if (defArray && defArray instanceof Array && defArray.length !== 0) {
            for (let defData of defArray) {
                let dd: IDataDefinition | undefined = DataDefinition.createFieldFromData(defData);
                if (dd) {
                    this.fields.push(dd);
                }
            }
        }

        let metadataArray = Reflect.get(data, "metadata");

        if (metadataArray) {
            if (Array.isArray(metadataArray)) {
                for (let m of metadataArray) {
                    this.fieldMetaData[Reflect.get(m, "fieldName")] = m;
                }
            }
        }

        this.value = Reflect.get(data, "value");
    }

    detectDataStructureChanges(
        newFields: IDataDefinition[],
        notDeepInChecked: boolean = false,
    ): DataStructureDifferences[] {
        return DataUtilities.compareDataDefinitions(this.fields, newFields, notDeepInChecked);
    }

    applyOwner() {
        this.fields.forEach((f) => {
            if (f instanceof ObjectDataDefinition) {
                f.applyOwner(this);
            } else {
                f.owner = this;
            }
        });
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        for (let f of this.fields) {
            let fissues = f.getDesignValidationIssues();
            if (fissues && fissues.length !== 0) {
                issues.push(new ValidationIssue("Field(s) have validation issues"));
                break;
            }
        }

        // check for duplicate names
        let names: string[] = [];
        for (let d of this.fields) {
            if (d.name && names.indexOf(d.name) !== -1) {
                issues.push(new ValidationIssue("Field " + d.name + " is duplicated"));
            } else {
                names.push(d.name);
            }
        }

        return issues;
    }
}
